import Languages from '../../../../../shared/types/enums/languages';
import PlayerUtils from '../../../../../shared/utils/player';
import HTTP_ERRORS from '../../../../../shared/types/enums/httpErrors';
import PLAYER_LICENSE_ERRORS from '../../../../../shared/types/enums/playerErrors';
import { IPlayerError } from '../../../../../shared/types/interfaces/error';
import { ITrack } from '../../../../../shared/types/interfaces/player';
import { PlayerManagerProps as Props } from '../../../../../shared/types/props/player';
import { store } from '../../../../../shared/store/store';
import { useCallback, useEffect, useRef } from 'react';
import {
  playerEndAction,
  updateNextEpisode,
} from '../../../../../shared/store/thunks/player.thunk';
import {
  PlayerState,
  PlayerType,
  PlayerError,
} from '../../../../../shared/types/enums/player';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../shared/store/hooks';
import {
  clearRetryPosition,
  mediaSelector,
  retry,
  setCurrentTime,
  setDuration,
  setError,
  setPosition,
  setRange,
  setReady,
  setState,
  setTracks,
} from '../../../../../shared/store/slices/media.slice';

/**
 * ListenerManager component for Dash.js Player.
 * Manages all event listeners for the Dash.js Player instance, including playback events,
 * error handling, quality changes, track changes, and analytics tracking.
 * Updates the Redux store with player state, position, duration, and track information.
 *
 * @param {Props} props - Component props containing Dash.js player instance, source, and playback parameters
 * @returns {null} This component doesn't render anything
 */
function ListenerManager({
  dash: player,
  source,
  bookmark,
  start,
  end,
}: Props) {
  const dispatch = useAppDispatch();
  const { audio, error, live, state, subtitles, userInteraction } =
    useAppSelector(mediaSelector);
  // Track if playback has ended to prevent multiple end events
  const endedRef = useRef(false);
  // Reference to retry timeout for error handling
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep user interaction state in ref for event handlers
  const userInteractionRef = useRef(userInteraction);
  userInteractionRef.current = userInteraction;

  // Updaters

  /**
   * Updates playback data including duration, position, and seek range.
   * Calculates position differently for live vs VOD content using Dash.js API.
   * Dispatches updates to Redux store for current time, duration, position, and range.
   */
  const updateData = useCallback(() => {
    // Calculate time data
    const duration =
      (live ? player?.getDVRWindowSize() : end || player?.duration()) || 0;

    const position = live
      ? Date.now() - (duration - (player?.time() || 0)) * 1000
      : Math.max(player?.time() || 0, bookmark || 0);

    const seek = live
      ? {
          start: Date.now() - (duration || 0) * 1000,
          end: Date.now(),
        }
      : { start: start || 0, end: duration };

    // If the end is surpassed, finish playback
    if (!endedRef.current && end && position > duration) {
      dispatch(playerEndAction());
      endedRef.current = true;
    }

    // Update media slice
    dispatch(setCurrentTime(player?.time() || 0));
    dispatch(setDuration(duration));
    dispatch(setRange({ seek }));
    dispatch(setPosition({ position, seek: false })); // Set position last is best
    dispatch(updateNextEpisode());
  }, [player, live, source]);

  /**
   * Updates the current audio track.
   * Finds and selects the audio track matching the selected audio ID.
   */
  const updateAudio = useCallback(() => {
    const track = player
      ?.getTracksFor('audio')
      .find((item) => item.id === audio.id);
    track && player?.setCurrentTrack(track);
  }, [audio, player]);

  /**
   * Updates current subtitle track and visibility.
   * Enables/disables subtitles based on track selection.
   */
  const updateSubtitles = useCallback(() => {
    const track = player
      ?.getTracksFor('text')
      .find((item) => item.id === subtitles.id);
    if (track) {
      player?.enableText(true);
      player?.setCurrentTrack(track);
    } else {
      player?.enableText(false);
    }
  }, [subtitles, player]);

  /**
   * Updates available audio and subtitle tracks from the player.
   * Maps Dash.js track information to the application's track format.
   */
  const updateTracks = useCallback(() => {
    // Set audio and subtitle tracks
    const audio =
      player?.getTracksFor('audio').map((item) => {
        return {
          id: item.id,
          language: item.lang?.toLowerCase() || Languages.ORIGINAL,
        } as ITrack;
      }) || [];
    const subtitles =
      player?.getTracksFor('text').map((item) => {
        return {
          id: item.id,
          language: item.lang?.toLowerCase() || Languages.ORIGINAL,
        } as ITrack;
      }) || [];
    dispatch(setTracks({ audio, subtitles }));
  }, [player, source]);

  // Effect hooks

  // Reset ready state when source changes or error occurs
  useEffect(() => {
    dispatch(setReady(false));
  }, [error, source]);

  // Update audio track when audio selection changes
  useEffect(() => {
    updateAudio();
  }, [audio]);

  // Update subtitle track when subtitle selection changes
  useEffect(() => {
    updateSubtitles();
  }, [subtitles]);

  // Main effect: Set up all Dash.js player event listeners
  useEffect(() => {
    const seeking = state === PlayerState.SEEKING || state === PlayerState.SEEK;

    /**
     * Handles the CAN_PLAY event when the player has enough data to start playing.
     * Marks player as ready, initializes tracks and playback data, and restores retry position if available.
     */
    const onCanPlay = () => {
      dispatch(setReady(true));
      updateData();
      updateTracks();
      if (error.retryPosition) {
        dispatch(clearRetryPosition());
        dispatch(setPosition({ position: error.retryPosition, seek: true }));
      }
    };

    /**
     * Handles the PLAYBACK_ENDED event when playback completes.
     * Prevents multiple end events from being dispatched.
     */
    const onEnded = () => {
      if (!endedRef.current) {
        dispatch(playerEndAction());
        endedRef.current = true;
      }
    };

    /**
     * Handles player errors with retry logic and error type detection.
     * Parses Dash.js error codes, HTTP errors, server errors, and geo-blocking errors.
     * Waits 30 seconds before attempting retry or dispatching error.
     */
    const onError = (event: any) => {
      dispatch(setState(PlayerState.ERROR));
      retryRef.current && clearTimeout(retryRef.current);
      retryRef.current = setTimeout(() => {
        const { media } = store.getState();
        if (media.state !== PlayerState.PLAYING) {
          if (!media.error.retry) {
            dispatch(retry());
          } else {
            const code = event.error?.code?.toString() || 'Unknown';
            const httpCode = (
              event?.error?.data as { responseCode: HTTP_ERRORS }
            )?.responseCode;
            const message = event.error.message;
            const serverError = PlayerUtils.getServerError(message);
            let type = PlayerUtils.getDashError(
              code,
              httpCode,
              serverError as PLAYER_LICENSE_ERRORS
            );
            if (event.error?.data?.response) {
              const geoBlockError = PlayerUtils.getGeoBlockError(
                event.error?.data?.response,
                true,
                event.error?.code,
                event.error?.data?.response?.status
              );
              if (geoBlockError) {
                type = geoBlockError as PlayerError;
              }
            }
            if (type) {
              const error: IPlayerError = {
                code,
                player: PlayerType.DASHJS,
                type,
              };
              dispatch(setError(error));
            }
          }
        }
      }, 30 * 1000);
    };

    /**
     * Handles the PLAYBACK_PAUSED event.
     */
    const onPause = () => {
      dispatch(setState(PlayerState.PAUSE));
    };

    /**
     * Handles the PLAYBACK_PLAYING event when playback is active.
     * Only updates state if not currently seeking.
     */
    const onPlaying = () => {
      !seeking && dispatch(setState(PlayerState.PLAYING));
    };

    /**
     * Handles the PLAYBACK_SEEKING event when seeking begins.
     */
    const onSeek = () => {
      dispatch(setState(PlayerState.SEEK));
    };

    /**
     * Handles the PLAYBACK_SEEKED event when seeking completes.
     */
    const onSeeked = () => {
      dispatch(setState(PlayerState.SEEKED));
    };

    /**
     * Handles the PLAYBACK_STALLED event when playback stalls due to buffering.
     * Only updates state if not in error state.
     */
    const onStalled = () => {
      const { media } = store.getState();
      if (media.state !== PlayerState.ERROR) {
        dispatch(setState(PlayerState.WAITING));
      }
    };

    /**
     * Handles the PLAYBACK_TIME_UPDATED event to update playback position.
     */
    const onTimeUpdate = () => {
      updateData();
    };

    /**
     * Handles the PLAYBACK_WAITING event when player is waiting for data.
     * Only updates state if not in error state.
     */
    const onWaiting = () => {
      const { media } = store.getState();
      if (media.state !== PlayerState.ERROR) {
        dispatch(setState(PlayerState.WAITING));
      }
    };

    // Register Dash.js player event listeners
    if (player) {
      player.on(dashjs.MediaPlayer.events.CAN_PLAY, onCanPlay);
      player.on(dashjs.MediaPlayer.events.ERROR, onError);
      player.on(dashjs.MediaPlayer.events.PLAYBACK_ENDED, onEnded);
      player.on(dashjs.MediaPlayer.events.PLAYBACK_PAUSED, onPause);
      player.on(dashjs.MediaPlayer.events.PLAYBACK_PLAYING, onPlaying);
      player.on(dashjs.MediaPlayer.events.PLAYBACK_SEEKING, onSeek);
      player.on(dashjs.MediaPlayer.events.PLAYBACK_SEEKED, onSeeked);
      player.on(dashjs.MediaPlayer.events.PLAYBACK_STALLED, onStalled);
      player.on(dashjs.MediaPlayer.events.PLAYBACK_TIME_UPDATED, onTimeUpdate);
      player.on(dashjs.MediaPlayer.events.PLAYBACK_WAITING, onWaiting);
    }

    // Cleanup: Remove all event listeners on unmount
    return () => {
      if (player) {
        player.off(dashjs.MediaPlayer.events.CAN_PLAY, onCanPlay);
        player.off(dashjs.MediaPlayer.events.ERROR, onError);
        player.off(dashjs.MediaPlayer.events.PLAYBACK_ENDED, onEnded);
        player.off(dashjs.MediaPlayer.events.PLAYBACK_PAUSED, onPause);
        player.off(dashjs.MediaPlayer.events.PLAYBACK_PLAYING, onPlaying);
        player.off(dashjs.MediaPlayer.events.PLAYBACK_SEEKING, onSeek);
        player.off(dashjs.MediaPlayer.events.PLAYBACK_SEEKED, onSeeked);
        player.off(dashjs.MediaPlayer.events.PLAYBACK_STALLED, onStalled);
        player.off(
          dashjs.MediaPlayer.events.PLAYBACK_TIME_UPDATED,
          onTimeUpdate
        );
        player.off(dashjs.MediaPlayer.events.PLAYBACK_WAITING, onWaiting);
      }
    };
  }, [player, source, state]);

  // This component doesn't render anything, it only manages event listeners
  return null;
}

export default ListenerManager;
